// TypeScript models mirroring the Swift Firestore schema.
// Source of truth: Checkover/Models/*.swift
// Keep in sync when iOS models change.

import type { Timestamp } from 'firebase/firestore';

export type MemberRole =
  | 'superadmin'
  | 'admin'
  | 'dispatcher'
  | 'manager'
  | 'driver'
  | 'subcontractor'
  | 'accounts'
  | 'viewer';

export const ALL_MEMBER_ROLES: MemberRole[] = [
  'superadmin',
  'admin',
  'dispatcher',
  'manager',
  'driver',
  'subcontractor',
  'accounts',
  'viewer',
];

export const ROLE_LABELS: Record<MemberRole, string> = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  manager: 'Manager',
  driver: 'Driver',
  subcontractor: 'Subcontractor',
  accounts: 'Accounts',
  viewer: 'Viewer',
};

export const MANAGER_ROLES: MemberRole[] = ['superadmin', 'admin'];
export const DISPATCH_ROLES: MemberRole[] = [
  'superadmin',
  'admin',
  'dispatcher',
  'manager',
];
export const JOB_CREATE_ROLES: MemberRole[] = [
  'superadmin',
  'admin',
  'dispatcher',
  'manager',
  'driver',
];

export function canManageCompany(role: MemberRole | undefined): boolean {
  return !!role && MANAGER_ROLES.includes(role);
}

/** Superadmin, admin, dispatcher and manager, as saveCustomer allows and the iOS
 *  app's MemberRole.canCreateCustomers (Bobby, 30 Sep 2026; was admins only). */
export function canManageCustomers(role: MemberRole | undefined): boolean {
  return canDispatchJobs(role);
}

export function canViewReports(role: MemberRole | undefined): boolean {
  return (
    !!role &&
    ['superadmin', 'admin', 'dispatcher', 'manager', 'accounts'].includes(role)
  );
}

export function canCreateJobs(role: MemberRole | undefined): boolean {
  return !!role && JOB_CREATE_ROLES.includes(role);
}

export function canDispatchJobs(role: MemberRole | undefined): boolean {
  return !!role && DISPATCH_ROLES.includes(role);
}

/** Who may set or change when a job is booked for: the office, and a driver on their own
 *  jobs (Bobby, 1 Nov 2026, "for now"). Mirrors functions/jobsCore.js editableFields. */
export function canBookJobs(role: MemberRole | undefined): boolean {
  return canDispatchJobs(role) || role === 'driver';
}

export function canPriceJobs(role: MemberRole | undefined): boolean {
  return !!role && ['superadmin', 'admin', 'accounts'].includes(role);
}

export function canInvoiceJobs(role: MemberRole | undefined): boolean {
  return !!role && ['superadmin', 'admin', 'accounts'].includes(role);
}

// Unlocking restores an invoiced job to priced. Managers may unlock, but the
// separate price-setting permission above still excludes them.
export function canUnlockInvoices(role: MemberRole | undefined): boolean {
  return !!role && ['superadmin', 'admin', 'manager'].includes(role);
}

export function canExcludeEvidence(role: MemberRole | undefined): boolean {
  return (
    !!role &&
    ['superadmin', 'admin', 'dispatcher', 'manager'].includes(role)
  );
}

// Who may SEE prices and invoicing (amounts, accounting status, price and
// invoice history entries): superadmin, admin, accounts and manager (decided
// 29 Sep 2026). An allow-list, so a role added later starts without it. Since 1 Nov
// 2026 the server enforces it too: prices live in jobFinance, which only these roles
// can read (firestore.rules), and the job keeps only accountingStatus.
export function canViewJobFinancials(role: MemberRole | undefined): boolean {
  return !!role && ['superadmin', 'admin', 'accounts', 'manager'].includes(role);
}

// Price and invoice history entries carry amounts or accounting state, so they
// are shown only where canViewJobFinancials allows.
export function isFinancialAuditEntry(log: Pick<JobAuditLog, 'actionType' | 'fieldChanged'>): boolean {
  return ['priceAdded', 'priceUpdated', 'jobInvoiced'].includes(log.actionType)
    || ['pricing', 'accountingStatus', 'invoiceReference'].includes(log.fieldChanged ?? '');
}

export function canViewJobHistory(role: MemberRole | undefined): boolean {
  return !!role && !['driver', 'subcontractor'].includes(role);
}

export function isReadOnlyForJobs(role: MemberRole | undefined): boolean {
  return !!role && ['accounts', 'viewer'].includes(role);
}

// PR-26: mirrors iOS JobState / functions/jobStateMachine.js (snake_case, D6).
// The legacy "in progress" (with a space) is gone with the two-collection model.
export type JobState =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'archived';

/** Work currently in progress; cancelled work has its own board filter. */
export const JOB_LIVE_STATES: JobState[] = ['pending', 'in_progress'];

/** Work still shown on the iOS Live Jobs board. */
export const JOB_BOARD_STATES: JobState[] = [
  'pending',
  'in_progress',
  'cancelled',
];

/** Finished work shown in the iOS Archive view. */
export const JOB_ARCHIVE_STATES: JobState[] = ['completed', 'archived'];

/** Human label for a state ("in_progress" -> "In progress"). */
export function jobStateLabel(state: JobState | string | null | undefined): string {
  if (!state) return 'Unknown';
  const normalized = String(state).trim().toLowerCase().replace(/\s+/g, '_');
  const labels: Record<string, string> = {
    pending: 'Pending',
    in_progress: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    archived: 'Archived',
  };
  return labels[normalized] ?? normalized;
}

/**
 * Legal transitions from a given state (mirrors functions/jobStateMachine.js).
 * The server re-validates every request; this only hides impossible buttons.
 * Archive/restore are handled by their own dedicated actions.
 */
export function legalTransitionsFrom(state: JobState): JobState[] {
  switch (state) {
    case 'pending':
      return ['in_progress', 'completed', 'cancelled'];
    case 'in_progress':
      return ['completed', 'cancelled', 'pending'];
    case 'completed':
      return ['in_progress', 'archived'];
    case 'cancelled':
      return ['pending', 'archived'];
    case 'archived':
      return ['completed'];
    default:
      return [];
  }
}

export interface CreatorInfo {
  uid: string;
  displayName: string;
}

export interface ImageSession {
  id: string;
  /** "collection" | "delivery" | "additional" */
  type: string;
  /** Seconds since epoch */
  timestamp: number;
  latitude: number;
  longitude: number;
  imageUrls: string[];
}

/** Per-image evidence metadata written by the backend finalise step (mirrors iOS JobImageEvidence). */
export interface JobImageEvidence {
  url: string;
  type?: string | null;
  imageId?: string | null;
  checksum?: string | null;
  uploadedAt?: Timestamp | null;
  capturedAt?: Timestamp | null;
  latitude?: number | null;
  longitude?: number | null;
  storagePath?: string | null;
  thumbnailURL?: string | null;
  thumbnailPath?: string | null;
}

/** True when an evidence image carries a usable GPS fix (finite, non-zero). */
export function imageHasValidLocation(
  e?: { latitude?: number | null; longitude?: number | null } | null
): boolean {
  const lat = e?.latitude;
  const lon = e?.longitude;
  return (
    lat != null &&
    lon != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    !(lat === 0 && lon === 0)
  );
}

export interface JobAttachment {
  id: string;
  jobId: string;
  companyId: string;
  fileName: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  storagePath: string;
  kind: 'file' | 'email';
  status: 'pendingUpload' | 'available' | 'failed' | 'deleted';
  uploadedBy: string;
  uploadedByDisplayName?: string | null;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  deletedAt?: Timestamp | null;
  deletedBy?: string | null;
}

/**
 * PR-26: the unified job document — companies/{companyId}/jobs/{jobId}.
 * Mirrors iOS Job.swift and functions/jobsCore.js buildNewJobDocument.
 * One document for the whole lifecycle; `state` replaces the old `status`
 * string and the recoveryJobs/archivedJobs collection split.
 */
export interface Job {
  id: string;
  schemaVersion?: number;
  jobNumber?: number | null;
  companyId: string;

  state: JobState;
  stateChangedAt?: Timestamp | null;
  stateChangedBy?: string | null;
  archivedAt?: Timestamp | null;
  completedAt?: Timestamp | null;

  registrationNumber: string;
  registrationKey?: string | null;
  make: string;
  model: string;
  colour: string;
  fuelType: string;
  transmission: string;
  vehicleMileage?: string | null;

  pickupLocation: string;
  dropoffLocation: string;
  collectionContactName?: string | null;
  collectionContactPhone?: string | null;
  collectionContactEmail?: string | null;
  deliveryContactName?: string | null;
  deliveryContactPhone?: string | null;
  deliveryContactEmail?: string | null;
  latitude?: number | null;
  longitude?: number | null;

  customerName: string;
  sourceAccount?: string | null;
  scheduledFor?: Timestamp | null;
  /** End of a booked time window (PR-43); with scheduledFor and scheduledDateOnly, one unit. */
  scheduledUntil?: Timestamp | null;
  /** A day booked with no time (PR-43). */
  scheduledDateOnly?: boolean | null;
  notes?: string | null;
  tags?: string[];

  courtesyCarRequired?: boolean;
  courtesyCarRegistration?: string | null;
  courtesyCarMileage?: string | null;

  imageUrls: string[];
  imageSessions: ImageSession[];
  typedImages?: JobImageEvidence[];
  imageCount?: number;

  accountingStatus?: 'unpriced' | 'priced' | 'invoiced' | string | null;
  pricing?: JobPricing | null;
  invoiceReference?: string | null;
  invoicedAt?: Timestamp | null;
  invoicedBy?: string | null;

  assignmentType?: 'internal' | 'subcontractor' | null;
  assignedUserId?: string | null;
  assignedUserDisplayName?: string | null;
  assignedSubcontractorId?: string | null;
  assignedSubcontractorName?: string | null;
  assignedAt?: Timestamp | null;
  assignedBy?: string | null;

  createdBy: CreatorInfo;
  createdAt?: Timestamp | null;
  clientCreatedAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export interface JobPricing {
  amountPence: number;
  breakdown?: PriceBreakdown | null;
  currency: string;
  notes?: string | null;
  pricedBy?: string | null;
  pricedAt?: Timestamp | null;
  status?: string | null;
}

export interface PricedCharge {
  id: string; code?: string | null; description?: string | null; quantity?: number | null;
  amountPence: number; flagId?: string | null;
}
export interface PriceBreakdown { jobAmountPence: number; charges: PricedCharge[]; }
export interface ChargeFlag {
  id: string; companyId: string; jobId: string;
  code?: string | null; description?: string | null; quantity?: number | null;
  claimedAmountPence?: number | null;
  status: 'open' | 'priced' | 'dismissed' | 'withdrawn';
  flaggedBy?: {uid: string; displayName?: string; role?: MemberRole};
  flaggedAt?: Timestamp | null;
}
export function canViewChargeFlags(role: MemberRole | undefined): boolean {
  return canViewJobFinancials(role) || role === 'dispatcher';
}

export interface CompanyMember {
  id: string;
  userId: string;
  email?: string;
  displayName?: string;
  role: MemberRole;
  companyId: string;
  subcontractorId?: string | null;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  isActive: boolean;
}

export interface Company {
  id: string;
  name?: string;
  createdAt?: Timestamp | null;
  features?: {
    accountsQueueEnabled?: boolean;
    contactEmailEnabled?: boolean;
    additionalChargesEnabled?: boolean;
  } | null;
}

export interface SubcontractorRecord {
  id: string;
  companyId: string;
  name: string;
  normalisedName?: string | null;
  shortName?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  isActive: boolean;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export interface UserDoc {
  uid: string;
  email?: string;
  displayName?: string;
  companyId?: string;
  companyIds?: string[];
  role?: MemberRole;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export interface Customer {
  id: string;
  companyId?: string;
  name?: string;
  normalizedName?: string;
  normalisedName?: string;
  nickname?: string;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyStatus?: string;
  notes?: string;
  isActive?: boolean | null;
  createdAt?: Timestamp | null;
  createdBy?: string;
  updatedAt?: Timestamp | null;
  updatedBy?: string;
}

// Matches JobAuditLog.swift
export type AuditActionType =
  | 'fieldUpdated'
  | 'noteAppended'
  | 'noteDeleted'
  | 'jobCreated'
  | 'jobArchived'
  | 'jobRestored'
  | 'statusChanged'
  | 'stateChanged'
  | 'jobEdited'
  | 'imageAdded'
  | 'imageExcluded'
  | 'imageReclassified'
  | 'attachmentAdded'
  | 'attachmentRemoved'
  | 'assignmentChanged'
  | 'assignmentCleared'
  | 'priceAdded'
  | 'priceUpdated'
  | 'jobInvoiced'
  | (string & {});

export interface JobAuditLog {
  id: string;
  jobId: string;
  companyId: string;
  userId: string;
  userDisplayName: string;
  userEmail?: string | null;
  timestamp: Timestamp;
  fieldChanged?: string | null;
  oldValue?: unknown;
  newValue?: unknown;
  actionType: AuditActionType;
  metadata?: Record<string, string> | null;
}

// lookupVehicle callable response
export interface VehicleLookupResponse {
  success: boolean;
  registration: string;
  mot: Array<Record<string, unknown>>;
  motVehicleInfo: {
    make: string | null;
    model: string | null;
    fuelType: string | null;
    primaryColour: string | null;
  } | null;
  dvla: Record<string, unknown> | null;
  timestamp: string;
  debug?: Record<string, unknown>;
}
